/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AdminUser } from "@deltabeacon/contracts";
import { api, setCsrfToken } from "./lib/api";

type AuthContextValue = {
  admin: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ admin: AdminUser; csrfToken: string }>("/auth/session")
      .then((data) => {
        setAdmin(data.admin);
        setCsrfToken(data.csrfToken);
      })
      .catch(() => {
        setAdmin(null);
        setCsrfToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      admin,
      loading,
      async login(email, password) {
        const data = await api<{ admin: AdminUser; csrfToken: string }>(
          "/auth/login",
          {
            method: "POST",
            body: JSON.stringify({ email, password }),
          },
        );
        setAdmin(data.admin);
        setCsrfToken(data.csrfToken);
      },
      async logout() {
        try {
          await api("/auth/logout", { method: "POST" });
        } finally {
          setAdmin(null);
          setCsrfToken(null);
        }
      },
      async refreshSession() {
        const data = await api<{ admin: AdminUser; csrfToken: string }>(
          "/auth/session",
        );
        setAdmin(data.admin);
        setCsrfToken(data.csrfToken);
      },
    }),
    [admin, loading],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
